<script lang="ts" setup>

	import Multiselect from 'vue-multiselect';
	import {useI18n} from 'vue-i18n';
	import {hasRoleMasterAccount, hasRoleSalesRep, type Order} from 'dummen-shared';
	import {computed, onBeforeMount, ref, watch} from 'vue';
	import {getOrdersWhereDeadlinesHaveNotPassed} from '@/service/order.service';

	const {t} = useI18n();

	const props = defineProps<{
		selectedCustomerId: string | null;
		preSelectedOrderId?: string | null;
		selectedExistingPurchaseOrder: Order | null;
	}>();

	const loading = ref(true);

	const existingOrders = ref<Order[]>([]);

	const emit = defineEmits(['on-selected-order-change']);

	const isMasterAccountOrSalesRep = computed(() => hasRoleMasterAccount() || hasRoleSalesRep());

	const getPurchaseOrderLabel = (purchaseOrder: Order) => {
		return purchaseOrder.poName ? purchaseOrder.poName : purchaseOrder.patId;
	};

	const updateSelectedOrder = (order: Order | null) => {
		emit('on-selected-order-change', order);
	};

	const setPreSelectedOrder = () => {
		const order = existingOrders.value.find((order: Order) => order.applicationId === props.preSelectedOrderId);

		if (order) {
			updateSelectedOrder(order);
		} else {
			updateSelectedOrder(null);
		}
	};

	const loadExistingOrders = async () => {
		loading.value = true;

		// Reset selected.
		updateSelectedOrder(null);

		const purchaseOrdersResponse: Order[] = isMasterAccountOrSalesRep.value
			? await getOrdersWhereDeadlinesHaveNotPassed(props.selectedCustomerId)
			: await getOrdersWhereDeadlinesHaveNotPassed();

		if (!purchaseOrdersResponse) {
			existingOrders.value = [];
			loading.value = false;
			return;
		}

		existingOrders.value = purchaseOrdersResponse;

		if (props.preSelectedOrderId) {
			setPreSelectedOrder();
		}

		loading.value = false;
	};

	watch(() => props.selectedCustomerId, async () => {
		await loadExistingOrders();
	});

	onBeforeMount(async () => {
		await loadExistingOrders();
	});

</script>

<template>
	<p v-if="loading">
		{{ t('shoppingcart.generic.loading.text') }}
	</p>

	<p v-else-if="!existingOrders || existingOrders.length === 0"
	   class="error">
		{{ t('shoppingcart.modal.createpo.content.noExistingOrders.error.text') }}
	</p>

	<div v-else class="input-wrapper">
		<label for="po-existing-order-select">
			{{ t('shoppingcart.modal.createpo.content.selectPO.label') }}:
		</label>

		<multiselect id="po-existing-order-select"
		             :custom-label="getPurchaseOrderLabel"
		             :model-value="selectedExistingPurchaseOrder"
		             :options="existingOrders"
		             :placeholder="t('shoppingcart.modal.createpo.content.selectPO.placeholder')"
		             :searchable="true"
		             :show-labels="true"
		             deselect-label=""
		             select-label=""
		             selected-label=""
		             track-by="applicationId"
		             @update:model-value="updateSelectedOrder" />
	</div>
</template>
